import { addSentryBreadcrumb } from '../../shared/sentry/sentryUser';
import { captureOpsFailure } from '../../shared/sentry/sentryOps';
import { logBusinessAuditEvent } from '../../shell/audit/auditLogs';
import { useSessionStore } from '../../shell/session/store/useSessionStore';
import { isCashSessionAlreadyOpenError } from '../../sync/core/repair/cashSessionHealErrors';
import { submitCashSessionCommand } from './submitCashSessionCommand';
import {
  reconcileCashOpenAfterRemoteConflict,
} from './cashSessionSyncCoordinator';
import { invalidateCashSummaryCaches } from './cashSummaryRemoteCache';
import type { CashSummary } from './cashSummaryQuery';
import { type CashSessionRow } from './cashSessionRow';
import { cashCommandErrorFromRaw, mapCashCommandError } from './mapCashCommandError';
import { newClientUuid } from '../../shared/newClientUuid';

export type { CashSessionRow } from './cashSessionRow';

async function getMergedActiveCashSession(
  branchId: string,
  options?: { force?: boolean },
): Promise<CashSessionRow | null> {
  const { getMergedActiveCashSession: resolve } = await import('./resolveCashSummary');
  return resolve(branchId, options);
}

async function resolveCashSummary(
  branchId: string,
  options?: { force?: boolean },
) {
  const { resolveCashSummary: resolve } = await import('./resolveCashSummary');
  return resolve(branchId, options);
}

/** Arqueo al cierre: fondo inicial + efectivo cobrado - egresos activos del turno. */
export function computeCashCloseReconciliation(
  openingAmount: number,
  sessionCashPaymentsTotal: number,
  closingAmount: number,
  sessionOutflowsTotal = 0
): { expectedAmount: number; difference: number } {
  const expectedAmount = openingAmount + sessionCashPaymentsTotal - sessionOutflowsTotal;
  return { expectedAmount, difference: closingAmount - expectedAmount };
}

export async function openCashSession({
  branchId,
  openingAmount,
  openedBy,
}: {
  branchId: string;
  openingAmount: number;
  openedBy?: string;
}): Promise<{ sessionId: string; alreadyOpen: boolean }> {
  const sessionId = newClientUuid();
  const openedAt = new Date().toISOString();

  const preSummary = await resolveCashSummary(branchId, { force: true });
  if (preSummary.activeSession?.status === 'open') {
    throw cashCommandErrorFromRaw('cash_session_already_open');
  }
  if (preSummary.remoteStatus === 'offline' || preSummary.remoteStatus === 'unavailable') {
    // Sin verificación remota el stub local reporta "sin turno" aunque el server
    // tenga una caja abierta (VUELTO-NEGOCIO-3Q): fail-closed, no abrir a ciegas.
    const error = cashCommandErrorFromRaw(
      preSummary.remoteStatus === 'offline' ? 'device_offline' : 'cash_state_unverified'
    );
    captureOpsFailure({
      module: 'cash',
      op: 'cash.open',
      error,
      // offline es esperado; 'unavailable' (online + fetch falló) se reporta
      // clasificado — es la señal de que el RPC de summary está fallando.
      expected: preSummary.remoteStatus === 'offline',
      ingestError: null,
      applied: false,
      enqueued: false,
      path: 'web-ram',
      itemId: sessionId,
      extra: { sessionId, branchId, code: error.code, remoteStatus: preSummary.remoteStatus },
    });
    throw error;
  }

  const submitResult = await submitCashSessionCommand('insert', {
    sessionId,
    branchId,
    openedAt,
    openedBy: openedBy ?? null,
    openingAmount,
    status: 'open',
  });

  const ingestAlreadyOpen = isCashSessionAlreadyOpenError(submitResult.ingestError ?? undefined);

  invalidateCashSummaryCaches(branchId);

  const adoptedSessionId = await reconcileCashOpenAfterRemoteConflict(sessionId, branchId, {
    knownAlreadyOpen: ingestAlreadyOpen,
  });
  if (adoptedSessionId) {
    const active = await getMergedActiveCashSession(branchId, { force: true });
    if (!active || active.id !== adoptedSessionId) {
      const error = cashCommandErrorFromRaw('cash_session_already_open');
      captureOpsFailure({
        module: 'cash',
        op: 'cash.open',
        error,
        expected: true,
        ingestError: submitResult.ingestError ?? null,
        applied: submitResult.applied,
        enqueued: submitResult.enqueued,
        path: 'web-ram',
        itemId: sessionId,
        extra: { sessionId, branchId, adoptedSessionId, code: error.code },
      });
      throw error;
    }
    return { sessionId: adoptedSessionId, alreadyOpen: true };
  }

  if (!submitResult.applied) {
    const ingestDetail = submitResult.ingestError?.trim() || 'cash_command_unknown';
    const mapped = mapCashCommandError(ingestDetail);
    const error = cashCommandErrorFromRaw(ingestDetail);
    captureOpsFailure({
      module: 'cash',
      op: 'cash.open',
      error,
      expected: mapped.expected === true,
      ingestError: submitResult.ingestError ?? null,
      applied: submitResult.applied,
      enqueued: submitResult.enqueued,
      path: 'web-ram',
      itemId: sessionId,
      extra: { sessionId, branchId, code: mapped.code },
    });
    throw error;
  }

  const activeAfterOpen = await getMergedActiveCashSession(branchId, { force: true });
  if (!activeAfterOpen || activeAfterOpen.status !== 'open') {
    const error = cashCommandErrorFromRaw('cash_session_not_open');
    captureOpsFailure({
      module: 'cash',
      op: 'cash.open',
      error,
      expected: true,
      ingestError: submitResult.ingestError ?? null,
      applied: submitResult.applied,
      enqueued: submitResult.enqueued,
      path: 'web-ram',
      itemId: sessionId,
      extra: { sessionId, branchId, code: error.code },
    });
    throw error;
  }

  const confirmedSessionId = activeAfterOpen.id;

  void logBusinessAuditEvent('reports.cash.open', {
    branchId,
    sessionId: confirmedSessionId,
    openingAmount,
    openedBy: openedBy ?? null,
  });

  addSentryBreadcrumb('cash_session', 'Caja abierta', 'info', {
    branchId,
    sessionId: confirmedSessionId,
    openingAmount,
  });

  return { sessionId: confirmedSessionId, alreadyOpen: false };
}

export async function closeCashSession({
  sessionId,
  closingAmount,
  note,
  closedBy,
  supervisorOverrideId,
  summary: prefetchedSummary,
}: {
  sessionId: string;
  closingAmount: number;
  note?: string;
  closedBy?: string;
  supervisorOverrideId?: string;
  /** Summary fresco ya resuelto por el caller — evita doble fetch. */
  summary?: CashSummary;
}): Promise<{ alreadyClosed: boolean }> {
  let session: any = null;
  let sessionCashTotal = 0;
  let sessionOutflowsTotal = 0;

  const branchId = useSessionStore.getState().currentBranchId;
  if (!branchId) {
    throw new Error('No se encontró la caja seleccionada.');
  }
  const summary = prefetchedSummary ?? (await resolveCashSummary(branchId, { force: true }));
  const active = summary.activeSession;
  if (!active || active.id !== sessionId) {
    throw new Error('No se encontró la caja seleccionada.');
  }
  if (active.status !== 'open') {
    throw cashCommandErrorFromRaw('cash_session_already_closed');
  }
  session = {
    id: active.id,
    branch_id: active.branchId,
    opening_amount: active.openingAmount,
    status: active.status,
  };
  sessionCashTotal = summary.sessionCashTotal;
  sessionOutflowsTotal = summary.sessionOutflowsTotal;

  const { expectedAmount, difference } = computeCashCloseReconciliation(
    session.opening_amount ?? 0,
    sessionCashTotal,
    closingAmount,
    sessionOutflowsTotal
  );

  const closedAt = new Date().toISOString();
  // ADR-017 Fase 4: cola eliminada — sin UPDATE SQLite local

  const closeSubmit = await submitCashSessionCommand('update', {
    sessionId,
    // Use a unique operationId for the close command to avoid inbox conflict
    // with the open command (which uses sessionId as operationId). The inbox
    // unique constraint is (organization_id, entity_kind, operation_id) —
    // without a distinct operationId, the close insert is skipped and the
    // existing open row's status='applied' triggers a false 'idempotent'.
    operationId: `${sessionId}:close`,
    status: 'closed',
    closedAt,
    closingAmount,
    expectedAmount,
    difference,
    note: note ?? null,
    closedBy: closedBy ?? null,
    ...(supervisorOverrideId ? { supervisorOverrideId } : {}),
  });

  if (!closeSubmit.applied) {
    // ADR-017 Fase 4: sin revertir SQLite local — el server es autoridad
    const ingestDetail = closeSubmit.ingestError?.trim() || 'cash_command_unknown';
    const mapped = mapCashCommandError(ingestDetail);
    const error = cashCommandErrorFromRaw(ingestDetail);
    captureOpsFailure({
      module: 'cash',
      op: 'cash.close',
      error,
      expected: mapped.expected === true,
      ingestError: closeSubmit.ingestError ?? null,
      applied: closeSubmit.applied,
      enqueued: closeSubmit.enqueued,
      path: 'web-ram',
      itemId: sessionId,
      extra: { sessionId, branchId: session.branch_id, code: mapped.code },
    });
    throw error;
  }

  // ADR-017 Fase 4: cola eliminada — sin flush post-cierre

  invalidateCashSummaryCaches(session.branch_id);

  void logBusinessAuditEvent('reports.cash.close', {
    branchId: session.branch_id,
    sessionId,
    openingAmount: session.opening_amount ?? 0,
    closingAmount,
    expectedAmount,
    difference,
    sessionCashTotal,
    outflowsTotal: sessionOutflowsTotal,
    note: note ?? null,
    closedBy: closedBy ?? null,
  });

  addSentryBreadcrumb('cash_session', 'Caja cerrada', 'info', {
    branchId: session.branch_id,
    sessionId,
    closingAmount,
    expectedAmount,
    difference,
  });

  const alreadyClosed = closeSubmit.ingestOutcome === 'idempotent';
  return { alreadyClosed };
}
